import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiClipboard, FiX, FiPlus, FiCheck, FiSlash, FiUpload, FiPaperclip } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = () => ({
  entity: '', period: '', expense_account: '', payment_account: '', currency: '',
  amount: '', expense_date: '', vendor: '', description: '', reference: '',
});

const Expenses = () => {
  const { isFinanceLeadership, user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, perRes, curRes, accRes, expRes] = await Promise.all([
        financeAPI.entities.list(),
        financeAPI.periods.list(),
        financeAPI.currencies.list(),
        financeAPI.accounts.list(),
        financeAPI.expenses.list(statusFilter ? { status: statusFilter } : {}),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setPeriods(perRes.data.results || perRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setExpenses(expRes.data.results || expRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load expenses.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.expenses.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create expense.');
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (expense, action) => {
    if (action === 'reject') {
      const reason = window.prompt('Reason for rejecting this expense?');
      if (!reason) return;
      setBusyId(expense.id);
      try {
        await financeAPI.expenses.reject(expense.id, reason);
        load();
      } catch (e) {
        setError(e?.response?.data?.error || 'Failed to reject expense.');
      } finally {
        setBusyId(null);
      }
      return;
    }
    setBusyId(expense.id);
    setError(null);
    try {
      if (action === 'submit') await financeAPI.expenses.submit(expense.id);
      else if (action === 'approve') await financeAPI.expenses.approve(expense.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || `Failed to ${action} expense.`);
    } finally {
      setBusyId(null);
    }
  };

  const uploadReceipt = async (expense, file) => {
    if (!file) return;
    setBusyId(expense.id);
    setError(null);
    try {
      await financeAPI.expenses.addReceipt(expense.id, file);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to upload receipt.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const expenseAccounts = accounts.filter((a) => a.account_type === 'EXPENSE' && (!form.entity || String(a.entity) === String(form.entity)));
  const paymentAccounts = accounts.filter((a) => ['ASSET', 'LIABILITY'].includes(a.account_type) && (!form.entity || String(a.entity) === String(form.entity)));
  const filteredPeriods = periods.filter((p) => !form.entity || String(p.entity) === String(form.entity));

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiClipboard size={22} />Expenses</h1>
          <p style={fs.pageSubtitle}>Submit for approval — approving posts a real ledger entry through the maker-checker path.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={fs.select}>
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="POSTED">Posted</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Expense</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}>
            <FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div>
            <div style={fs.label}>Entity</div>
            <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value, period: '', expense_account: '', payment_account: '' })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Period</div>
            <select required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>
              {filteredPeriods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Expense Category</div>
            <select required value={form.expense_account} onChange={(e) => setForm({ ...form, expense_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>
              {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Paid Via (Cash/Bank or Payable)</div>
            <select required value={form.payment_account} onChange={(e) => setForm({ ...form, payment_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>
              {paymentAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Currency</div>
            <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>
              {currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Amount</div>
            <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div>
            <div style={fs.label}>Expense Date</div>
            <input required type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div>
            <div style={fs.label}>Vendor</div>
            <input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={fs.label}>Description</div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create Draft'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : expenses.length === 0 ? (
        <div style={fs.emptyState}><FiClipboard size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No expenses yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {expenses.map((exp) => {
            const busy = busyId === exp.id;
            const color = fs.STATUS_COLORS[exp.status] || '#888';
            const canSubmit = exp.status === 'DRAFT' && exp.created_by === user?.id;
            const canApprove = exp.status === 'SUBMITTED' && isFinanceLeadership();
            return (
              <div key={exp.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{exp.vendor || 'Expense'}</span>
                      <span style={fs.badge(color)}>{exp.status}</span>
                      <span style={{ color: fs.colors.textBright, fontWeight: 700 }}>{fs.money(exp.amount, exp.currency)}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      {entityName(exp.entity)} · {exp.expense_date} {exp.description ? `· ${exp.description}` : ''}
                    </div>
                    {exp.status === 'REJECTED' && exp.rejection_reason && (
                      <div style={{ color: fs.colors.red, fontSize: 12, marginTop: 6 }}>Rejected: {exp.rejection_reason}</div>
                    )}
                    {exp.receipts?.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {exp.receipts.map((r) => (
                          <a key={r.id} href={r.file} target="_blank" rel="noreferrer" style={{ color: fs.colors.blue, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                            <FiPaperclip size={11} />{r.file_name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {exp.status === 'DRAFT' && (
                      <label style={{ ...fs.neutralButton, cursor: 'pointer' }}>
                        <FiUpload size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Receipt
                        <input type="file" hidden onChange={(e) => uploadReceipt(exp, e.target.files[0])} />
                      </label>
                    )}
                    {canSubmit && (
                      <button onClick={() => doAction(exp, 'submit')} disabled={busy} style={fs.primaryButton}>Submit</button>
                    )}
                    {canApprove && (
                      <>
                        <button onClick={() => doAction(exp, 'approve')} disabled={busy} style={fs.successButton}><FiCheck size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Approve</button>
                        <button onClick={() => doAction(exp, 'reject')} disabled={busy} style={fs.dangerButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Reject</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Expenses;
